import React, { useState } from "react"
import { asDialogData } from "../../../core/utils/utils"
import {
    Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions,
    DialogContent, DialogTitle, FormControlLabel, IconButton, Paper, Stack, TextField, Typography
} from "@mui/material"
import { DataGrid } from "@mui/x-data-grid"
import EditIcon from "@mui/icons-material/Edit"
import DeleteIcon from "@mui/icons-material/Delete"
import AddIcon from "@mui/icons-material/Add"
import {
    useGetAllOrderCommentTemplatesQuery,
    useCreateOrderCommentTemplateMutation,
    useUpdateOrderCommentTemplateMutation,
    useDeleteOrderCommentTemplateMutation,
} from "../../../core/api/order-comment-api"
import ResponseDialog from "../../ResponseDialog"

// Templates for the auto-comment that is posted to the Asana task
// (and duplicated in Slack and the personal account) when an order moves to the "Ready" status.
export default function OrderCommentTemplatesAdmin() {
    const { data: templates = [], isLoading } = useGetAllOrderCommentTemplatesQuery()
    const [createTemplate, createResp] = useCreateOrderCommentTemplateMutation()
    const [updateTemplate, updateResp] = useUpdateOrderCommentTemplateMutation()
    const [deleteTemplate] = useDeleteOrderCommentTemplateMutation()

    const [editing, setEditing] = useState(null)
    const [form, setForm] = useState({ name: "", text: "", active: true })
    const [respOpen, setRespOpen] = useState(false)
    const [actionResp, setActionResp] = useState({})

    function openNew() {
        setEditing({})
        setForm({ name: "", text: "", active: true })
    }
    function openEdit(row) {
        setEditing(row)
        setForm({ name: row.name, text: row.text || "", active: row.active })
    }
    function close() { setEditing(null) }

    async function save() {
        const resp = editing.id
            ? await updateTemplate({ id: editing.id, ...form })
            : await createTemplate(form)
        setActionResp(asDialogData(resp)); setRespOpen(true); close()
    }
    async function remove(row) {
        if (!window.confirm(`Delete template "${row.name}"?`)) return
        const resp = await deleteTemplate(row.id)
        setActionResp(asDialogData(resp)); setRespOpen(true)
    }

    const columns = [
        { field: "name", headerName: "Name", width: 160 },
        { field: "text", headerName: "Text", flex: 1 },
        { field: "active", headerName: "Status", width: 110, renderCell: (p) =>
            <Chip size="small" label={p.value ? "active" : "inactive"} color={p.value ? "success" : "default"} />
        },
        { field: "actions", headerName: "", width: 100, renderCell: (p) =>
            <Stack direction="row">
                <IconButton size="small" onClick={() => openEdit(p.row)}><EditIcon fontSize="small" /></IconButton>
                <IconButton size="small" onClick={() => remove(p.row)}><DeleteIcon fontSize="small" /></IconButton>
            </Stack>
        },
    ]

    if (isLoading) return <Box sx={{ py: 4, textAlign: "center" }}><CircularProgress /></Box>

    return (
        <Box sx={{ py: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h5">Order auto-replies</Typography>
                <Button variant="contained" color="warning" startIcon={<AddIcon />} onClick={openNew}>
                    New template
                </Button>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                The text is automatically added as a comment to the order task when it moves to the "Ready" status,
                duplicated in the Slack notification to the employee, and shown when confirming receipt in the personal account.
                A template is attached to a product in "Products → Edit product".
            </Typography>
            <Paper sx={{ height: 560 }}>
                <DataGrid
                    rows={templates}
                    columns={columns}
                    pageSize={25}
                    rowsPerPageOptions={[10, 25, 50]}
                    disableRowSelectionOnClick
                />
            </Paper>

            <Dialog open={!!editing} onClose={close} maxWidth="sm" fullWidth>
                <DialogTitle>{editing?.id ? "Edit template" : "New template"}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField label="Name" value={form.name}
                                   onChange={e => setForm({ ...form, name: e.target.value })} required />
                        <TextField label="Auto-comment text" value={form.text}
                                   onChange={e => setForm({ ...form, text: e.target.value })}
                                   required multiline minRows={4} />
                        <FormControlLabel
                            control={<Checkbox checked={form.active}
                                               onChange={e => setForm({ ...form, active: e.target.checked })} />}
                            label="Active (available for selection in products)"
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={close}>Cancel</Button>
                    <Button variant="contained" color="warning" onClick={save}
                            disabled={createResp.isLoading || updateResp.isLoading ||
                                form.name.trim() === "" || form.text.trim() === ""}>
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            <ResponseDialog
                open={respOpen}
                onClose={() => setRespOpen(false)}
                responseData={actionResp}
                label="Template"
                information="Saved"
            />
        </Box>
    )
}
